// Queries the SPA writes for the user, so a menu choice (a class, a property,
// count or breakdown) never asks anyone to write QQL or SPARQL. QROS speaks
// QQL (FIND); RDF speaks SPARQL (SELECT).

const RDFS_LABEL = 'http://www.w3.org/2000/01/rdf-schema#label';

/** A term as a query names it: a core term (`:label`) as written, anything
 *  else as a full IRI in angle brackets. */
export function termRef(uri: string): string {
  return /^:[\w.-]+$/.test(uri) ? uri : `<${uri}>`;
}

/** Every statement about each instance of a class: the rows a table of the
 *  class is built from. The labels its names read by are not taken here, as
 *  the query reads only the picked graphs and a label may live in another. */
export function instanceStatementsQuery(classUri: string, qros: boolean): string {
  const c = termRef(classUri);
  return qros
    ? `FIND ?s ?p ?o WHERE { ?s :type ${c} ?s ?p ?o }`
    : `SELECT ?s ?p ?o WHERE { ?s a ${c} . ?s ?p ?o }`;
}

/** A count card: how many instances a class has. */
export function countQuery(classUri: string, qros: boolean): string {
  const c = termRef(classUri);
  return qros
    ? `FIND COUNT(?x) AS ?count WHERE { ?x :type ${c} }`
    : `SELECT (COUNT(DISTINCT ?x) AS ?count) WHERE { ?x a ${c} }`;
}

/** A breakdown card: a class's instances counted by the value of one
 *  property. A property whose values are things gives a bar list of those
 *  things by name (a distribution card); a property whose values are text,
 *  numbers or dates gives a table of each value and its count. The variable
 *  names keep the count ahead of the label in the server's reading order. */
export function breakdownQuery(
  classUri: string,
  propertyUri: string,
  valuesAreThings: boolean,
  qros: boolean,
): string {
  const c = termRef(classUri);
  const p = termRef(propertyUri);
  if (valuesAreThings) {
    return qros
      ? `FIND ?value ?label COUNT(?x) AS ?count WHERE { ?x :type ${c} ?x ${p} ?value OPTIONAL { ?value :label ?label } } GROUP BY ?value ?label ORDER BY ?count DESC LIMIT 10`
      : `SELECT ?value ?label (COUNT(DISTINCT ?x) AS ?count) WHERE { ?x a ${c} . ?x ${p} ?value . OPTIONAL { ?value <${RDFS_LABEL}> ?label } } GROUP BY ?value ?label ORDER BY DESC(?count) LIMIT 10`;
  }
  return qros
    ? `FIND ?value COUNT(?x) AS ?count WHERE { ?x :type ${c} ?x ${p} ?value } GROUP BY ?value ORDER BY ?count DESC LIMIT 50`
    : `SELECT ?value (COUNT(DISTINCT ?x) AS ?count) WHERE { ?x a ${c} . ?x ${p} ?value } GROUP BY ?value ORDER BY DESC(?count) LIMIT 50`;
}
